import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpCacheManager } from '@ngneat/cashew';
import { DateTimeService } from '@y42/date-time';
import { environment } from '@y42/environment';
import { JobStatus } from '@y42/json-schema';
import { ORG_API_URL_PARAM } from '@y42/model';
import { retryStrategy } from '@y42/rxjs';
import { takePromise } from '@y42/util/take-promise';
import { firstValueFrom, retry } from 'rxjs';
import { JobContext, JobDateRange, JobDetails, JobHttpModel, JobWithParentHttpModel } from '../../../state';

export interface JobRequestOld {
  created_at: string;
  event_id?: string;
  target: JobTargetOld;
  context: JobContext;
  table_settings: { [key: string]: unknown };
  source_settings?: { [key: string]: unknown };
  source_credentials: string;
  target_credentials?: string;
}

export interface JobHttpModelOld<
  Request extends JobRequestOld = JobRequestOld,
  Details extends JobDetails = JobDetails,
> {
  path: string;
  company_id: string;
  created_at: string;
  details: Details;
  error?: {
    message: string;
  };
  finished_at?: string;
  request: Request;
  request_id: string;
  status: JobStatus;
  import_status: JobStatus;
  parent_job_id: string;
}

export interface JobTargetOld {
  storage: { [key: string]: unknown };
  info: { [key: string]: unknown };
}

type JobWithParentHttpModelOld<
  Request extends JobRequestOld = JobRequestOld,
  Details extends JobDetails = JobDetails,
> = JobHttpModelOld<Request, Details> & { parent_job?: JobHttpModelOld<Request, Details> };

type SingleJobResponse<
  HttpModel extends JobHttpModel = JobHttpModel,
  HttpModelOld extends JobHttpModelOld = JobHttpModelOld,
> = HttpModel | HttpModelOld;

interface MultipleJobsResponse<
  HttpModel extends JobHttpModel = JobHttpModel,
  HttpModelOld extends JobHttpModelOld = JobHttpModelOld,
> {
  jobs: Array<HttpModel | HttpModelOld>;
  cursor?: string;
}

export interface AllJobsRequestParams {
  dateRange?: JobDateRange;
  limit?: number;
  cacheKey?: string;
}

export interface AllJobsResponse<HttpModel extends JobHttpModel = JobHttpModel> {
  jobs: HttpModel[];
  cursor?: string;
  fromCache: boolean;
}

export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_SCALING_DURATION_MS = 5000;
export const DEFAUL_RETRY_STRATEGY = {
  maxRetryAttempts: MAX_RETRY_ATTEMPTS,
  scalingDuration: RETRY_SCALING_DURATION_MS,
} as const;

@Injectable()
export class BaseJobHttpService<
  HttpModel extends JobHttpModel = JobHttpModel,
  HttpModelOld extends JobHttpModelOld = JobHttpModelOld,
> implements JobHttpServiceInterface<HttpModel>
{
  constructor(
    private readonly http: HttpClient,
    private readonly dateTimeService: DateTimeService,
    private readonly httpCacheManager: HttpCacheManager,
  ) {}

  readonly url = `${environment.url.job_info_api}`;

  getLatestValidJobs(featureOrFeatureChildPaths: string[]) {
    return this._getLatestValidJobs(featureOrFeatureChildPaths).then((jobs) =>
      jobs.map((job) => this.mapJobsVersion(job)),
    );
  }

  getLatestJobs(featureOrFeatureChildPaths: string[]) {
    return this._getLatestJobs(featureOrFeatureChildPaths).then((jobs) => jobs.map((job) => this.mapJobsVersion(job)));
  }

  async getAllJobs(
    featureOrFeatureChildPaths: string[],
    options?: AllJobsRequestParams,
  ): Promise<AllJobsResponse<HttpModel>> {
    const cacheKeyPropertyFallback = {
      cacheKey: '', // caching is disabled if there is no cacheKey, no need to have any fallback
      dateRange: {
        from: 'no-from',
        to: 'no-to',
      },
      limit: 'no-limit',
    };
    const cacheKey = options?.cacheKey
      ? [
          options.cacheKey,
          options?.limit ?? cacheKeyPropertyFallback.limit,
          options?.dateRange?.from?.toISOString() ?? cacheKeyPropertyFallback.dateRange.from,
          options?.dateRange?.to?.toISOString() ?? cacheKeyPropertyFallback.dateRange.to,
          ...featureOrFeatureChildPaths,
        ].join('|')
      : undefined;

    if (cacheKey) {
      const cachedValue = this.httpCacheManager.get<{
        jobs: HttpModel[];
        fromCache: boolean;
      }>(cacheKey)?.body;
      if (cachedValue) {
        return cachedValue;
      }
    }

    return this._getAllJobs(featureOrFeatureChildPaths, options).then((jobsResponse) => {
      const result = {
        ...jobsResponse,
        jobs: jobsResponse.jobs.map((job) => this.mapJobsVersion(job)),
        fromCache: false,
      };

      if (cacheKey) {
        this.httpCacheManager.set(cacheKey, { ...result, fromCache: true });
      }

      return result;
    });
  }

  async getJob(requestId: string, path: string): Promise<HttpModel> {
    return this._getJob(requestId, path).then((job) => this.mapJobsVersion(job));
  }

  private _getLatestValidJobs(featureOrFeatureChildPaths: string[]) {
    return this.getJobs({ featureOrFeatureChildPaths, validOnly: true, includeParents: true });
  }

  private _getLatestJobs(featureOrFeatureChildPaths: string[]) {
    return this.getJobs({ featureOrFeatureChildPaths, validOnly: false, includeParents: true });
  }

  private async _getAllJobs(
    featureOrFeatureChildPaths: string[],
    options?: AllJobsRequestParams & { cursor?: string },
  ): Promise<{ jobs: Array<HttpModel | HttpModelOld>; cursor?: string }> {
    if (!featureOrFeatureChildPaths.length) {
      return { jobs: [] };
    }

    let params = new HttpParams();

    if (options?.dateRange?.from) {
      params = params.append('after', options.dateRange.from.toISOString());
    }
    if (options?.dateRange?.to) {
      params = params.append('before', options.dateRange.to.toISOString());
    }
    if (options?.cursor) {
      params = params.append('cursor', options.cursor);
    }
    if (options?.limit) {
      params = params.append('limit', options.limit);
    }

    const response = await takePromise(
      this.http
        .get<MultipleJobsResponse<HttpModel, HttpModelOld>>(`${this.url}/company/${ORG_API_URL_PARAM}/jobs`, {
          params,
          headers: this.prepareHeaders(featureOrFeatureChildPaths),
        })
        .pipe(retry({ delay: retryStrategy(DEFAUL_RETRY_STRATEGY) })),
    );

    return response;
  }

  private async _getJob(requestId: string, path: string): Promise<HttpModel | HttpModelOld> {
    return firstValueFrom(
      this.http
        .get<SingleJobResponse<HttpModel, HttpModelOld>>(`${this.url}/company/${ORG_API_URL_PARAM}/job/${requestId}`, {
          headers: this.prepareHeaders([path]),
        })
        .pipe(retry({ delay: retryStrategy(DEFAUL_RETRY_STRATEGY) })),
    );
  }

  private async getJobs(options: {
    featureOrFeatureChildPaths: string[];
    validOnly: boolean;
    includeParents: boolean;
  }): Promise<Array<HttpModel | HttpModelOld>> {
    if (!options.featureOrFeatureChildPaths.length) {
      return [];
    }

    const params = new HttpParams({
      fromObject: {
        valid_only: options.validOnly,
        include_parents: options.includeParents,
      },
    });

    const response = await firstValueFrom(
      this.http
        .get<MultipleJobsResponse<HttpModel, HttpModelOld>>(`${this.url}/company/${ORG_API_URL_PARAM}/latest-jobs`, {
          headers: this.prepareHeaders(options.featureOrFeatureChildPaths),
          params,
        })
        .pipe(retry({ delay: retryStrategy(DEFAUL_RETRY_STRATEGY) })),
    );

    return options.includeParents ? this.jobsWithParents(response.jobs) : response.jobs;
  }

  private prepareHeaders(pathString: string[]): HttpHeaders {
    return new HttpHeaders({
      'x-storage-location': pathString.map((path) => encodeURI(path)),
    });
  }

  private jobsWithParents(jobs: Array<HttpModel | HttpModelOld>): Array<HttpModel | HttpModelOld> {
    const jobsWithParents = [...jobs];
    jobs.forEach((job: JobWithParentHttpModel | JobWithParentHttpModelOld) => {
      if (job.parent_job) {
        jobsWithParents.push(job.parent_job as HttpModel | HttpModelOld);
        delete job.parent_job;
      }
    });
    return jobsWithParents.sort((a, b) => this.dateTimeService.diff(b.created_at, a.created_at));
  }

  private mapJobsVersion(oldJob: HttpModelOld | HttpModel): HttpModel {
    const newJob = oldJob as unknown as HttpModel;

    if ((oldJob.request as JobRequestOld).target_credentials) {
      newJob.request.environment_secret_id = (oldJob.request as JobRequestOld).target_credentials;
    }

    if ((oldJob.request as JobRequestOld).source_credentials) {
      newJob.request.integration_secret_id = (oldJob.request as JobRequestOld).source_credentials;
    }

    if ((oldJob.request.target as JobTargetOld | undefined)?.storage) {
      const target = oldJob.request.target as JobTargetOld;
      newJob.request.storage = {
        id: target.storage.id as string,
        branch: target.storage.branch as string,
        path: target.storage.path as string,
        company_id: target.storage.company_id as string,
        commit_sha: target.storage.commit_sha as string,
      };
    }
    return newJob;
  }
}

@Injectable({ providedIn: 'root', useClass: BaseJobHttpService })
export abstract class JobHttpServiceInterface<HttpModel extends JobHttpModel = JobHttpModel> {
  abstract readonly url: string;
  abstract getLatestValidJobs(featureOrFeatureChildPaths: string[]): Promise<HttpModel[]>;
  abstract getLatestJobs(featureOrFeatureChildPaths: string[]): Promise<HttpModel[]>;
  abstract getAllJobs(
    featureOrFeatureChildPaths: string[],
    options?: { dateRange?: JobDateRange; cursor?: string; limit?: number; cacheKey?: string },
  ): Promise<{ jobs: HttpModel[]; cursor?: string; fromCache: boolean }>;
  abstract getJob(requestId: string, path: string): Promise<HttpModel>;
}
